import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { TopBanner } from "./TopBanner";
import { useLanguage } from "@/hooks/use-language";
import { ORDER_CTAS_ENABLED, TOP_BANNER_ENABLED } from "@/config/orderCtas";

interface LayoutProps {
  children: React.ReactNode;
}

const SITE_ORIGIN = "https://praedigo.com";
const SUPPORTED_LANGS = ["en", "de"] as const;

/** Map a pathname (with /:lang prefix) to a SEO key under `seo.pages.*`. */
function routeKeyForPath(pathname: string): string {
  // Strip leading /lang
  const stripped = pathname.replace(/^\/(en|de)/, "") || "/";
  if (stripped === "/" || stripped === "") return "home";
  const segs = stripped.split("/").filter(Boolean);
  // Nested comorbidity sub-routes: comorbidities/ckd → "comorbidities/ckd"
  if (segs[0] === "comorbidities" && segs[1]) {
    return `comorbidities/${segs[1]}`;
  }
  return segs[0] ?? "home";
}

/** Path without language prefix, normalised with leading slash. */
function pathWithoutLang(pathname: string): string {
  const stripped = pathname.replace(/^\/(en|de)/, "");
  return stripped === "" ? "/" : stripped;
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function Layout({ children }: LayoutProps) {
  const { currentLang } = useLanguage();
  const { t } = useTranslation();
  const { pathname } = useLocation();

  useEffect(() => {
    const key = routeKeyForPath(pathname);
    const title =
      t(`seo.pages.${key}.title`, { defaultValue: t("seo.pages.home.title") });
    const description =
      t(`seo.pages.${key}.description`, {
        defaultValue: t("seo.defaultDescription"),
      });

    document.title = title;
    document.documentElement.lang = currentLang;

    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:locale", currentLang === "de" ? "de_DE" : "en_US");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);

    const restPath = pathWithoutLang(pathname);
    const canonicalHref = `${SITE_ORIGIN}/${currentLang}${
      restPath === "/" ? "" : restPath
    }`;

    upsertLink("canonical", canonicalHref);
    upsertMeta("property", "og:url", canonicalHref);

    // hreflang alternates for both supported languages + x-default
    SUPPORTED_LANGS.forEach((lng) => {
      const href = `${SITE_ORIGIN}/${lng}${restPath === "/" ? "" : restPath}`;
      upsertLink("alternate", href, lng);
    });
    upsertLink(
      "alternate",
      `${SITE_ORIGIN}/en${restPath === "/" ? "" : restPath}`,
      "x-default"
    );
  }, [currentLang, pathname, t]);

  const restPath = pathWithoutLang(pathname);
  const isHome = restPath === "/";
  const hasBanner =
    TOP_BANNER_ENABLED &&
    ORDER_CTAS_ENABLED &&
    (isHome || restPath === "/product" || restPath === "/comorbidities");

  return (
    <div className="min-h-screen flex flex-col" data-has-banner={hasBanner ? "true" : undefined}>
      {hasBanner && <TopBanner />}
      <Navbar hasBanner={hasBanner} />
      <main className={`flex-1 ${hasBanner ? "pt-[6.5rem]" : "pt-16"}`}>{children}</main>
      <div aria-hidden="true" className="bg-white w-full" style={{ height: "2cm" }} />
      <Footer />
    </div>
  );
}
